// CanvasActivity — May 7, 2026
// /admin/canvas-activity/:userId  (manager-only)
//
// Day-by-day breadcrumb of a canvasser's shift. Mapbox map showing the GPS
// ping trail, every visit overlaid as a chip with on-property/near/far
// distance flag, and a chronological visits list. Date picker switches
// between days.
//
// Data sources:
//   - canvas_shifts (one row per shift; we pick all shifts on the chosen date)
//   - canvas_gps_pings (~5-min ping rate while on shift)
//   - canvas_visits (each knock log) + knock_pins (for distance check)
//   - team_roster (rep name)

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

declare const mapboxgl: any;

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const MANAGER_ROLES = new Set([
  "admin","sales_director","asm","team_lead",
  "restore_manager","roof_team_manager","roof_director","director_of_operations",
]);

interface Shift {
  id: string;
  started_at: string;
  ended_at: string | null;
  visit_count: number;
  contact_count: number;
  appointments_set: number;
  miles_walked: number | null;
  ping_count: number;
}
interface Ping { id: number; pinged_at: string; lat: number; lon: number; accuracy_m: number | null; battery_pct: number | null; }
interface Visit {
  id: string;
  visited_at: string;
  outcome: string;
  notes: string | null;
  lat: number | null;
  lon: number | null;
  knock_pin_id: string | null;
  pin_lat?: number | null;
  pin_lon?: number | null;
  pin_address?: string | null;
}

const OUTCOME_COLOR: Record<string, string> = {
  no_answer:    "#fbbf24",
  contact_made: "#9333ea",
  follow_up:    "#9333ea",
  appt_set:     "#ff9e7a",
  rent:         "#94a3b8",
  wrong_address:"#94a3b8",
  do_not_knock: "#475569",
};

function fmtDate(d: Date): string {
  // Local YYYY-MM-DD
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function dayBoundaries(yyyymmdd: string): { start: string; end: string } {
  // Local-day window expressed as ISO; canvas_shifts stamps in UTC, but
  // Postgres compares with timezone correctly.
  const [y,m,d] = yyyymmdd.split("-").map(Number);
  const start = new Date(y, m-1, d, 0, 0, 0, 0);
  const end = new Date(y, m-1, d+1, 0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}
function feet(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000;
  const toRad = (x: number) => (x*Math.PI)/180;
  const dLat = toRad(bLat-aLat), dLon = toRad(bLon-aLon);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(aLat))*Math.cos(toRad(bLat))*Math.sin(dLon/2)**2;
  return 2*R*Math.asin(Math.min(1, Math.sqrt(a)))*3.28084;
}

export default function CanvasActivity() {
  const { userId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { role, isSuperUser } = useAuth();

  const isManager = isSuperUser || (role && MANAGER_ROLES.has(role));

  const dateStr = params.get("date") ?? fmtDate(new Date());
  const [repName, setRepName] = useState<string>("");
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [pings, setPings] = useState<Ping[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);

  // Aggregate stats across all shifts on the day
  const totals = useMemo(() => {
    const knocks = shifts.reduce((a, s) => a + (s.visit_count || 0), 0);
    const contacts = shifts.reduce((a, s) => a + (s.contact_count || 0), 0);
    const appts = shifts.reduce((a, s) => a + (s.appointments_set || 0), 0);
    const miles = shifts.reduce((a, s) => a + Number(s.miles_walked || 0), 0);
    const hours = shifts.reduce((a, s) => {
      const end = s.ended_at ? new Date(s.ended_at).getTime() : Date.now();
      const start = new Date(s.started_at).getTime();
      return a + (end - start) / 3600000;
    }, 0);
    return { knocks, contacts, appts, miles, hours };
  }, [shifts]);

  // ── Fetch data on date / userId change ────────────────────────────────
  useEffect(() => {
    if (!userId || !isManager) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const { start, end } = dayBoundaries(dateStr);

      // Rep name
      const { data: roster } = await supabase
        .from("team_roster").select("full_name").eq("id", userId).maybeSingle();
      if (!cancelled) setRepName(roster?.full_name ?? "Unknown rep");

      // Shifts that overlap the day
      const { data: shiftRows, error: sErr } = await supabase
        .from("canvas_shifts")
        .select("id, started_at, ended_at, visit_count, contact_count, appointments_set, miles_walked, ping_count")
        .eq("user_id", userId)
        .gte("started_at", start)
        .lt("started_at", end)
        .order("started_at", { ascending: true });
      if (cancelled) return;
      if (sErr) { setErr(sErr.message); setLoading(false); return; }

      const shiftIds = (shiftRows ?? []).map((s: any) => s.id);
      setShifts((shiftRows ?? []) as Shift[]);

      // Pings + visits — bounded by shift IDs OR by date if no shifts
      let pingRows: Ping[] = [];
      let visitRows: any[] = [];
      if (shiftIds.length) {
        const [{ data: pp }, { data: vv }] = await Promise.all([
          supabase
            .from("canvas_gps_pings")
            .select("id, pinged_at, lat, lon, accuracy_m, battery_pct")
            .in("shift_id", shiftIds)
            .order("pinged_at", { ascending: true }),
          supabase
            .from("canvas_visits")
            .select("id, visited_at, outcome, notes, lat, lon, knock_pin_id, pin:knock_pins!canvas_visits_knock_pin_id_fkey(lat, lon, property_street)")
            .eq("visited_by", userId)
            .gte("visited_at", start)
            .lt("visited_at", end)
            .order("visited_at", { ascending: true }),
        ]);
        pingRows = (pp ?? []) as Ping[];
        visitRows = (vv ?? []) as any[];
      }
      if (cancelled) return;
      setPings(pingRows);
      setVisits(
        visitRows.map((v) => ({
          ...v,
          pin_lat: v.pin?.lat ?? null,
          pin_lon: v.pin?.lon ?? null,
          pin_address: v.pin?.property_street ?? null,
        }))
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, dateStr, isManager]);

  // ── Init Mapbox ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDiv.current || mapRef.current || !MAPBOX_TOKEN) return;
    if (typeof mapboxgl === "undefined") return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-84.5, 34.0],
      zoom: 9,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
    return () => { try { map.remove(); } catch {} mapRef.current = null; };
  }, []);

  // ── Render breadcrumb + visits when data lands ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      // Breadcrumb line
      const coords = pings.map((p) => [Number(p.lon), Number(p.lat)] as [number, number]);
      const lineFeature = {
        type: "Feature" as const,
        properties: {},
        geometry: { type: "LineString" as const, coordinates: coords },
      };
      if (!map.getSource("activity-line")) {
        map.addSource("activity-line", { type: "geojson", data: lineFeature });
        map.addLayer({
          id: "activity-line",
          type: "line",
          source: "activity-line",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#3b82f6", "line-width": 3, "line-opacity": 0.7 },
        });
        map.addLayer({
          id: "activity-pings",
          type: "circle",
          source: "activity-line",
          paint: { "circle-radius": 0 }, // line layer carries the breadcrumb
        });
      } else {
        (map.getSource("activity-line") as any).setData(lineFeature);
      }

      // Visits — colored circles, click → small popup
      const visitFc = {
        type: "FeatureCollection" as const,
        features: visits
          .filter((v) => v.lat != null && v.lon != null)
          .map((v) => ({
            type: "Feature" as const,
            properties: {
              outcome: v.outcome,
              color: OUTCOME_COLOR[v.outcome] ?? "#94a3b8",
              addr: v.pin_address ?? "",
              when: v.visited_at,
            },
            geometry: { type: "Point" as const, coordinates: [Number(v.lon), Number(v.lat)] },
          })),
      };
      if (!map.getSource("activity-visits")) {
        map.addSource("activity-visits", { type: "geojson", data: visitFc });
        map.addLayer({
          id: "activity-visits-pts",
          type: "circle",
          source: "activity-visits",
          paint: {
            "circle-radius": 7,
            "circle-color": ["get", "color"],
            "circle-stroke-color": "#fff",
            "circle-stroke-width": 2,
          },
        });
        map.on("click", "activity-visits-pts", (e: any) => {
          const f = e.features?.[0]; if (!f) return;
          const p = f.properties ?? {};
          const c = f.geometry?.coordinates;
          new mapboxgl.Popup()
            .setLngLat(c)
            .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${p.outcome}</strong><br/>${p.addr ?? ""}<br/><small>${p.when?.slice(0,16).replace("T"," ")}</small></div>`)
            .addTo(map);
        });
        map.on("mouseenter", "activity-visits-pts", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "activity-visits-pts", () => { map.getCanvas().style.cursor = ""; });
      } else {
        (map.getSource("activity-visits") as any).setData(visitFc);
      }

      // Auto-fit
      if (coords.length > 0) {
        const bounds = coords.reduce(
          (b: any, c: [number, number]) => b.extend(c),
          new mapboxgl.LngLatBounds(coords[0], coords[0]),
        );
        for (const v of visits) if (v.lat != null && v.lon != null) bounds.extend([Number(v.lon), Number(v.lat)]);
        map.fitBounds(bounds, { padding: 50, duration: 600, maxZoom: 16 });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("style.load", apply);
  }, [pings, visits]);

  function shiftDay(delta: number) {
    const [y,m,d] = dateStr.split("-").map(Number);
    const next = new Date(y, m-1, d+delta);
    const nextStr = fmtDate(next);
    const p = new URLSearchParams(params);
    p.set("date", nextStr);
    setParams(p);
  }

  if (!isManager) {
    return (
      <div className="mx-auto max-w-md p-6">
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          Manager-only audit dashboard. Ask your manager for access.
        </div>
        <button onClick={() => navigate("/")} className="mt-3 rounded-md bg-slate-900 px-3 py-2 text-sm text-white">← Home</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/canvas/manager-audit")} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Audit</button>
            <div>
              <div className="text-base font-bold text-slate-900">{repName}</div>
              <div className="text-xs text-slate-500">Per-rep GPS audit · {dateStr}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shiftDay(-1)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">← Prev day</button>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => {
                const p = new URLSearchParams(params);
                p.set("date", e.target.value);
                setParams(p);
              }}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700"
            />
            <button onClick={() => shiftDay(+1)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Next day →</button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-3 p-4">
        {/* Day stats strip */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Tile label="Hours" value={totals.hours.toFixed(1)} />
          <Tile label="Knocks" value={totals.knocks} />
          <Tile label="Contacts" value={totals.contacts} />
          <Tile label="Appts" value={totals.appts} />
          <Tile label="Miles" value={totals.miles.toFixed(1)} />
        </div>

        {err ? <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">⚠ {err}</div> : null}

        {/* Map */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              GPS breadcrumb · {pings.length} pings · {visits.length} visits
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-500">
              <span><span className="inline-block h-1 w-3 align-middle" style={{background:"#3b82f6"}}/> path</span>
              <span><span className="inline-block h-2 w-2 rounded-full align-middle" style={{background:"#fbbf24"}}/> no answer</span>
              <span><span className="inline-block h-2 w-2 rounded-full align-middle" style={{background:"#9333ea"}}/> contact</span>
              <span><span className="inline-block h-2 w-2 rounded-full align-middle" style={{background:"#ff9e7a"}}/> appt</span>
            </div>
          </div>
          <div ref={mapDiv} className="h-[420px] w-full" />
        </div>

        {/* Visits list */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Visits this day
          </div>
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
          ) : visits.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No visits logged on this date.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visits.map((v) => {
                const ft = (v.lat != null && v.lon != null && v.pin_lat != null && v.pin_lon != null)
                  ? feet(v.lat, v.lon, v.pin_lat, v.pin_lon)
                  : null;
                const chip = ft == null ? null
                  : ft <= 50  ? { lab: `🟢 ${Math.round(ft)} ft`, cls: "bg-emerald-100 text-emerald-800" }
                  : ft <= 300 ? { lab: `🟡 ${Math.round(ft)} ft`, cls: "bg-amber-100 text-amber-800" }
                  :             { lab: `🔴 ${Math.round(ft)} ft`, cls: "bg-red-100 text-red-800" };
                return (
                  <div key={v.id} className="px-4 py-2 text-xs">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-mono text-slate-400">{v.visited_at.slice(11,16)}</span>
                      <span className="rounded px-1.5 py-0.5 font-semibold text-white" style={{ background: OUTCOME_COLOR[v.outcome] ?? "#94a3b8" }}>{v.outcome}</span>
                      {chip ? <span className={`rounded px-1.5 py-0.5 font-semibold ${chip.cls}`}>{chip.lab}</span> : null}
                      <span className="text-slate-700">{v.pin_address ?? "(no pin)"}</span>
                    </div>
                    {v.notes ? <div className="mt-0.5 text-slate-500">{v.notes}</div> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-2xl font-bold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
